import type {
  CalculatorInputs,
  CalculatorResults,
  PMIInfo,
  BreakdownItem,
  TaxEstimate,
  AffordabilityResult,
  GuidelineRule,
  CashToCloseItem,
  CashToCloseResult,
  AmortizationYearRow,
  AmortizationMonthRow,
  LoanType,
  CreditScoreRange,
} from './types';

// ── Colors for chart segments ──
export const CHART_COLORS = {
  pi: '#2563eb',
  tax: '#7c3aed',
  ins: '#059669',
  pmi: '#d97706',
  hoa: '#6366f1',
  k401: '#dc2626',
  startup: '#0891b2',
  mcc: '#16a34a',
} as const;

// ── Core Math ──

export function calcMonthlyPayment(principal: number, annualRate: number, years: number): number {
  if (principal <= 0) return 0;
  if (annualRate <= 0) return principal / (years * 12);
  const r = annualRate / 100 / 12;
  const n = years * 12;
  return principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

export function getPMIRate(ltv: number, creditScore: CreditScoreRange, loanType: LoanType): PMIInfo {
  if (loanType === 'va') {
    return { rate: 0, label: 'VA - No PMI required', note: 'VA loans do not require PMI. VA funding fee may apply.' };
  }
  if (loanType === 'usda') {
    return { rate: 0.35, label: 'USDA Annual Fee: 0.35%', note: 'USDA guarantee fee: 1% upfront + 0.35% annual.' };
  }
  if (loanType === 'fha') {
    const rate = ltv > 95 ? 0.85 : 0.80;
    return {
      rate,
      label: `FHA MIP: ${rate}%/yr`,
      note: 'FHA MIP is required for the life of the loan if down payment < 10%. With 10%+ down, MIP drops off after 11 years.',
    };
  }

  // Conventional PMI
  if (ltv <= 80) {
    return { rate: 0, label: 'No PMI required (LTV ≤ 80%)', note: '' };
  }

  let rate: number;
  const score = parseInt(creditScore);
  if (score >= 760)      rate = ltv > 95 ? 0.58 : ltv > 90 ? 0.41 : ltv > 85 ? 0.25 : 0.19;
  else if (score >= 740) rate = ltv > 95 ? 0.71 : ltv > 90 ? 0.53 : ltv > 85 ? 0.34 : 0.26;
  else if (score >= 720) rate = ltv > 95 ? 0.90 : ltv > 90 ? 0.65 : ltv > 85 ? 0.44 : 0.33;
  else if (score >= 700) rate = ltv > 95 ? 1.05 : ltv > 90 ? 0.81 : ltv > 85 ? 0.54 : 0.43;
  else if (score >= 680) rate = ltv > 95 ? 1.24 : ltv > 90 ? 0.95 : ltv > 85 ? 0.67 : 0.55;
  else if (score >= 660) rate = ltv > 95 ? 1.45 : ltv > 90 ? 1.15 : ltv > 85 ? 0.86 : 0.72;
  else                   rate = ltv > 95 ? 1.72 : ltv > 90 ? 1.36 : ltv > 85 ? 1.05 : 0.90;

  return {
    rate,
    label: `PMI: ~${rate.toFixed(2)}%/yr`,
    note: 'PMI can be removed once LTV reaches 80% (automatic at 78%).',
  };
}

// ── Net Income Estimation (MN, Single filer, Standard Deduction) ──

export function estimateNetMonthly(grossAnnual: number): TaxEstimate {
  if (grossAnnual <= 0) {
    return { net: 0, netAnnual: 0, federal: 0, state: 0, fica: 0, totalTax: 0, effectiveRate: 0 };
  }

  // Standard deduction 2024 (single)
  const standardDeduction = 14600;
  const taxableIncome = Math.max(0, grossAnnual - standardDeduction);

  // Federal brackets 2024 (single)
  const fedBrackets: [number, number][] = [
    [11600, 0.10],
    [47150 - 11600, 0.12],
    [100525 - 47150, 0.22],
    [191950 - 100525, 0.24],
    [243725 - 191950, 0.32],
    [609350 - 243725, 0.35],
    [Infinity, 0.37],
  ];
  let fedTax = 0;
  let remaining = taxableIncome;
  for (const [bracket, rate] of fedBrackets) {
    if (remaining <= 0) break;
    const taxable = Math.min(remaining, bracket);
    fedTax += taxable * rate;
    remaining -= taxable;
  }

  // MN state brackets 2024 (single)
  const mnBrackets: [number, number][] = [
    [31690, 0.0535],
    [104090 - 31690, 0.068],
    [193240 - 104090, 0.0785],
    [Infinity, 0.0985],
  ];
  const mnTaxableIncome = Math.max(0, grossAnnual - 14575); // MN standard deduction ~$14,575
  let stateTax = 0;
  remaining = mnTaxableIncome;
  for (const [bracket, rate] of mnBrackets) {
    if (remaining <= 0) break;
    const taxable = Math.min(remaining, bracket);
    stateTax += taxable * rate;
    remaining -= taxable;
  }

  // FICA: SS 6.2% on first $168,600 + Medicare 1.45%
  const ssTax = Math.min(grossAnnual, 168600) * 0.062;
  const medicareTax = grossAnnual * 0.0145;
  const fica = ssTax + medicareTax;

  const totalTax = fedTax + stateTax + fica;
  const netAnnual = grossAnnual - totalTax;
  const effectiveRate = (totalTax / grossAnnual) * 100;

  return {
    net: netAnnual / 12,
    netAnnual,
    federal: fedTax,
    state: stateTax,
    fica,
    totalTax,
    effectiveRate,
  };
}

// ── Months until LTV reaches 80% ──

export function calcMonthsUntilLTV80(
  principal: number,
  rate: number,
  term: number,
  homePrice: number
): number {
  const target = homePrice * 0.80;
  const r = rate / 100 / 12;
  const n = term * 12;
  const pmt = calcMonthlyPayment(principal, rate, term);
  let balance = principal;
  for (let m = 1; m <= n; m++) {
    const interest = balance * r;
    const princ = pmt - interest;
    balance -= princ;
    if (balance <= target) return m;
  }
  return n;
}

// ── Amortization ──

export function buildYearlyAmortization(
  principal: number,
  rate: number,
  term: number
): AmortizationYearRow[] {
  const r = rate / 100 / 12;
  const n = term * 12;
  const pmt = calcMonthlyPayment(principal, rate, term);
  let balance = principal;
  const rows: AmortizationYearRow[] = [];
  let yearPrinc = 0;
  let yearInt = 0;

  for (let m = 1; m <= n; m++) {
    const interest = balance * r;
    const princ = pmt - interest;
    balance = Math.max(0, balance - princ);
    yearPrinc += princ;
    yearInt += interest;
    if (m % 12 === 0 || m === n) {
      rows.push({
        year: Math.ceil(m / 12),
        principal: yearPrinc,
        interest: yearInt,
        balance,
      });
      yearPrinc = 0;
      yearInt = 0;
    }
  }
  return rows;
}

export function buildMonthlyAmortization(
  principal: number,
  rate: number,
  term: number,
  months: number = 12
): AmortizationMonthRow[] {
  const r = rate / 100 / 12;
  const n = term * 12;
  const pmt = calcMonthlyPayment(principal, rate, term);
  let balance = principal;
  const rows: AmortizationMonthRow[] = [];

  for (let m = 1; m <= Math.min(months, n); m++) {
    const interest = balance * r;
    const princ = pmt - interest;
    balance = Math.max(0, balance - princ);
    rows.push({ month: m, payment: pmt, principal: princ, interest, balance });
  }
  return rows;
}

// ── Cash to Close ──

function calcCashToClose(inputs: CalculatorInputs, principal: number, monthlyTax: number, monthlyInsurance: number, rate: number): CashToCloseResult {
  const closingCosts = principal * (inputs.closingCostPct / 100);

  // Auto-estimate prepaids if left at 0
  let prepaids = inputs.prepaids;
  if (prepaids === 0) {
    const prepaidTax = monthlyTax * 3;
    const prepaidIns = monthlyInsurance * 14;
    const prepaidInterest = principal * (rate / 100 / 365) * 15;
    prepaids = prepaidTax + prepaidIns + prepaidInterest;
  }

  // MN Mortgage Registration Tax ($2.30 per $1000)
  const mnMortgageRegTax = principal * 0.0023;

  // Upfront fees by loan type
  let upfrontFee = 0;
  let upfrontFeeLabel = '';
  if (inputs.loanType === 'fha') {
    upfrontFee = principal * 0.0175;
    upfrontFeeLabel = 'FHA Upfront MIP (1.75%)';
  } else if (inputs.loanType === 'va') {
    const dpRatio = inputs.downPaymentAmt / inputs.homePrice;
    const vaFeeRate = dpRatio >= 0.10 ? 0.0125 : dpRatio >= 0.05 ? 0.015 : 0.0215;
    upfrontFee = principal * vaFeeRate;
    upfrontFeeLabel = `VA Funding Fee (${(vaFeeRate * 100).toFixed(2)}%)`;
  } else if (inputs.loanType === 'usda') {
    upfrontFee = principal * 0.01;
    upfrontFeeLabel = 'USDA Guarantee Fee (1%)';
  }

  const loan401kApplied = inputs.use401k ? inputs.loan401kAmount : 0;
  const startUpApplied = inputs.useStartUp ? inputs.startUpLoanAmt : 0;

  const items: CashToCloseItem[] = [
    { label: 'Down Payment', value: inputs.downPaymentAmt, type: 'cost' },
    { label: `Closing Costs (${inputs.closingCostPct}% of loan)`, value: closingCosts, type: 'cost' },
    { label: 'Prepaids & Escrow', value: prepaids, type: 'cost', sub: inputs.prepaids === 0 ? 'Auto-estimated' : '' },
    { label: 'MN Mortgage Registration Tax', value: mnMortgageRegTax, type: 'cost' },
  ];

  if (upfrontFee > 0) {
    items.push({ label: upfrontFeeLabel, value: upfrontFee, type: 'cost', sub: 'Can often be financed into loan' });
  }

  items.push({ label: '', value: 0, type: 'divider' });

  if (inputs.sellerConcession > 0) items.push({ label: 'Seller Concession', value: -inputs.sellerConcession, type: 'credit' });
  if (loan401kApplied > 0) items.push({ label: '401(k) Loan Proceeds', value: -loan401kApplied, type: 'credit' });
  if (startUpApplied > 0) items.push({ label: 'MN Start Up Loan', value: -startUpApplied, type: 'credit' });

  const totalCosts = items.filter(i => i.type === 'cost').reduce((s, i) => s + i.value, 0);
  const totalCredits = inputs.sellerConcession + loan401kApplied + startUpApplied;
  const cashToClose = totalCosts - totalCredits;

  return { items, cashToClose, totalCosts, totalCredits };
}

// ── Affordability ──

function calcAffordability(
  inputs: CalculatorInputs,
  totalMonthly: number
): AffordabilityResult | null {
  const grossMonthly = inputs.grossAnnualIncome / 12;
  if (grossMonthly <= 0) return null;

  const otherDebts = inputs.monthlyCarPayment + inputs.monthlyStudentLoans + inputs.monthlyCreditCards + inputs.monthlyOtherDebt;
  const housingExpense = totalMonthly;
  const totalObligations = housingExpense + otherDebts;
  const frontDti = (housingExpense / grossMonthly) * 100;
  const backDti = (totalObligations / grossMonthly) * 100;
  const remainingIncome = grossMonthly - totalObligations;

  // Verdict
  let verdictClass: string, verdictIcon: string, verdictText: string, verdictSub: string;
  if (backDti <= 36 && frontDti <= 28) {
    verdictClass = 'verdict-good'; verdictIcon = '✅';
    verdictText = 'Comfortably Affordable';
    verdictSub = 'Your ratios are within conservative guidelines. Lenders will view this favorably.';
  } else if (backDti <= 43 && frontDti <= 31) {
    verdictClass = 'verdict-ok'; verdictIcon = '👍';
    verdictText = 'Affordable — Within Standard Limits';
    verdictSub = 'Meets most conventional loan guidelines. You should qualify with good credit.';
  } else if (backDti <= 50 && frontDti <= 36) {
    verdictClass = 'verdict-stretch'; verdictIcon = '⚠️';
    verdictText = 'Stretching — Approval Possible';
    verdictSub = 'Some lenders allow this with strong compensating factors (high credit, reserves). Budget will be tight.';
  } else {
    verdictClass = 'verdict-bad'; verdictIcon = '🚫';
    verdictText = 'Over Typical Limits';
    verdictSub = 'Exceeds standard DTI limits. Consider a lower price, larger down payment, or paying down debts.';
  }

  // Lender guidelines
  const loanTypeLabel = inputs.loanType === 'fha' ? 'FHA' : inputs.loanType === 'va' ? 'VA' : inputs.loanType === 'usda' ? 'USDA' : 'Conventional';
  const frontLimit = inputs.loanType === 'fha' ? 31 : inputs.loanType === 'va' ? 41 : 28;
  const backLimit = inputs.loanType === 'fha' ? 43 : inputs.loanType === 'va' ? 41 : inputs.loanType === 'usda' ? 41 : 43;

  const lenderGuidelines: GuidelineRule[] = [
    { name: 'Front-End (28% rule)', limit: '≤ 28%', you: frontDti.toFixed(1) + '%', pass: frontDti <= 28 },
    { name: `${loanTypeLabel} Front-End`, limit: `≤ ${frontLimit}%`, you: frontDti.toFixed(1) + '%', pass: frontDti <= frontLimit },
    { name: 'Back-End (36% rule)', limit: '≤ 36%', you: backDti.toFixed(1) + '%', pass: backDti <= 36 },
    { name: `${loanTypeLabel} Back-End`, limit: `≤ ${backLimit}%`, you: backDti.toFixed(1) + '%', pass: backDti <= backLimit },
    { name: 'Stretched Limit', limit: '≤ 50%', you: backDti.toFixed(1) + '%', pass: backDti <= 50 },
  ];

  // Net income
  const taxes = estimateNetMonthly(inputs.grossAnnualIncome);
  const netMonthly = taxes.net;
  const housingPctNet = netMonthly > 0 ? (housingExpense / netMonthly * 100) : 0;
  const debtPctNet = netMonthly > 0 ? (otherDebts / netMonthly * 100) : 0;
  const leftover = netMonthly - housingExpense - otherDebts;
  const leftoverPct = netMonthly > 0 ? (leftover / netMonthly * 100) : 0;
  const dailyLeft = leftover / 30;
  const weeklyLeft = leftover / 4.33;

  // Nudge
  let nudgeClass: string, nudgeEmoji: string, nudgeComment: string;
  if (leftoverPct >= 40) {
    nudgeClass = 'verdict-good'; nudgeEmoji = '🟢';
    nudgeComment = 'Comfortable cushion for groceries, utilities, savings, and unexpected expenses.';
  } else if (leftoverPct >= 30) {
    nudgeClass = 'verdict-ok'; nudgeEmoji = '🟡';
    nudgeComment = 'Reasonable room for essentials, but budget carefully for savings goals.';
  } else if (leftoverPct >= 20) {
    nudgeClass = 'verdict-stretch'; nudgeEmoji = '🟠';
    nudgeComment = 'Tight. Covers basics but leaves little for savings or emergencies.';
  } else {
    nudgeClass = 'verdict-bad'; nudgeEmoji = '🔴';
    nudgeComment = leftover > 0
      ? 'Very tight. Groceries, utilities, gas, and basics will consume nearly everything.'
      : 'Your obligations exceed estimated take-home pay.';
  }

  return {
    frontDti,
    backDti,
    housingExpense,
    otherDebts,
    totalObligations,
    remainingIncome,
    verdictClass,
    verdictIcon,
    verdictText,
    verdictSub,
    lenderGuidelines,
    taxes,
    netMonthly,
    housingPctNet,
    debtPctNet,
    leftover,
    leftoverPct,
    dailyLeft,
    weeklyLeft,
    nudgeClass,
    nudgeEmoji,
    nudgeComment,
  };
}

// ── Main Calculator ──

export function calculateAll(inputs: CalculatorInputs): CalculatorResults {
  const { homePrice, downPaymentAmt, interestRate, loanTerm, loanType, creditScore, propertyTaxRate, homeInsurance, hoaFees } = inputs;

  const principal = Math.max(0, homePrice - downPaymentAmt);
  const monthlyPI = calcMonthlyPayment(principal, interestRate, loanTerm);
  const monthlyTax = homePrice * (propertyTaxRate / 100) / 12;
  const monthlyInsurance = homeInsurance / 12;
  const ltv = homePrice > 0 ? (principal / homePrice * 100) : 0;
  const pmiInfo = getPMIRate(ltv, creditScore, loanType);
  const monthlyPMI = principal * (pmiInfo.rate / 100) / 12;

  // 401(k) loan
  let monthly401k = 0;
  if (inputs.use401k) {
    monthly401k = calcMonthlyPayment(inputs.loan401kAmount, inputs.loan401kRate, inputs.loan401kTerm);
  }

  // MN Start Up
  let monthlyStartUp = 0;
  if (inputs.useStartUp) {
    monthlyStartUp = inputs.startUpLoanAmt / (10 * 12); // 0% over 10 years
  }

  // MCC
  let mccMonthly = 0;
  let mccAnnualCredit = 0;
  if (inputs.useMCC) {
    const annualInterest = principal * (interestRate / 100);
    mccAnnualCredit = Math.min(annualInterest * 0.35, 2000);
    mccMonthly = mccAnnualCredit / 12;
  }

  const totalBeforeMCC = monthlyPI + monthlyTax + monthlyInsurance + monthlyPMI + hoaFees + monthly401k + monthlyStartUp;
  const totalMonthly = totalBeforeMCC - mccMonthly;

  // Breakdown items
  const breakdownItems: BreakdownItem[] = [
    { label: 'Principal & Interest', value: monthlyPI, color: CHART_COLORS.pi },
    { label: 'Property Tax', value: monthlyTax, color: CHART_COLORS.tax },
    { label: "Homeowner's Insurance", value: monthlyInsurance, color: CHART_COLORS.ins },
  ];
  if (monthlyPMI > 0) breakdownItems.push({ label: pmiInfo.label.split(':')[0], value: monthlyPMI, color: CHART_COLORS.pmi });
  if (hoaFees > 0) breakdownItems.push({ label: 'HOA Fees', value: hoaFees, color: CHART_COLORS.hoa });
  if (monthly401k > 0) breakdownItems.push({ label: '401(k) Loan Payment', value: monthly401k, color: CHART_COLORS.k401 });
  if (monthlyStartUp > 0) breakdownItems.push({ label: 'MN Start Up Loan', value: monthlyStartUp, color: CHART_COLORS.startup });
  if (mccMonthly > 0) breakdownItems.push({ label: 'MCC Tax Credit', value: -mccMonthly, color: CHART_COLORS.mcc });

  // Stats
  const totalInterest = (monthlyPI * loanTerm * 12) - principal;
  const totalPIPaid = monthlyPI * loanTerm * 12;
  const interestToPrincipalRatio = principal > 0 ? (totalInterest / principal) * 100 : 0;

  // Affordability
  const affordability = calcAffordability(inputs, totalMonthly);

  // Cash to close
  const cashToClose = calcCashToClose(inputs, principal, monthlyTax, monthlyInsurance, interestRate);

  // Amortization
  const yearlyAmortization = buildYearlyAmortization(principal, interestRate, loanTerm);
  const monthlyAmortization = buildMonthlyAmortization(principal, interestRate, loanTerm, 12);
  const monthsUntilLTV80 = calcMonthsUntilLTV80(principal, interestRate, loanTerm, homePrice);

  return {
    loanAmount: principal,
    ltv,
    monthlyPI,
    monthlyTax,
    monthlyInsurance,
    monthlyPMI,
    pmiInfo,
    monthly401k,
    monthlyStartUp,
    mccMonthly,
    mccAnnualCredit,
    totalMonthly,
    totalBeforeMCC,
    breakdownItems,
    totalInterest,
    totalPIPaid,
    interestToPrincipalRatio,
    affordability,
    cashToClose,
    yearlyAmortization,
    monthlyAmortization,
    monthsUntilLTV80,
  };
}

/** Default inputs matching the original HTML defaults */
export function getDefaultInputs(): CalculatorInputs {
  return {
    homePrice: 350000,
    downPaymentPct: 10,
    downPaymentAmt: 35000,
    interestRate: 6.75,
    loanTerm: 30,
    loanType: 'conventional',
    creditScore: '740',
    propertyTaxRate: 1.15,
    homeInsurance: 1800,
    hoaFees: 0,
    propertyAddress: '',
    propertyCity: '',
    propertyZip: '',
    mnCounty: '1.15',
    use401k: false,
    loan401kAmount: 25000,
    loan401kRate: 5.5,
    loan401kTerm: 5,
    useStartUp: false,
    startUpLoanAmt: 17000,
    useMCC: false,
    grossAnnualIncome: 85000,
    monthlyCarPayment: 0,
    monthlyStudentLoans: 0,
    monthlyCreditCards: 0,
    monthlyOtherDebt: 0,
    closingCostPct: 3.0,
    sellerConcession: 0,
    earnestMoney: 0,
    prepaids: 0,
  };
}
